<template>
	<div class="dx-swatch-additional side-navigation-menu" @click="forwardClick">
		<slot />
		<div class="menu-container">
			<dx-tree-view ref="treeViewRef" :data-source="items" key-expr="path" selection-mode="single" @item-context-menu="treeViewContextMenu"
				:focus-state-enabled="false" expand-event="click" @item-click="handleItemClick" width="100%" />
			<DxContextMenu ref="contextMenuRef" v-model:data-source="contextMenuItems" target=".menu-container .dx-treeview-item" @item-click="contextMenuItemClick" />
		</div>
	</div>
</template>

<script>
import DxTreeView from 'devextreme-vue/tree-view';
import { DxContextMenu } from 'devextreme-vue/context-menu';
import { sizes } from '../utils/media-query';
import navigation from '../app-navigation';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GroupCache from '../groups';
import Page from '../pages';

export default {
	props: {
		compactMode: Boolean
	},
	setup(props, context) {
		const route = useRoute();
		const router = useRouter();

		const isLargeScreen = sizes()['screen-large'];
		const items = navigation.map((item) => {
			if (item.path && !(/^\//.test(item.path))) {
				item.path = `/${item.path}`;
			}
			return { ...item, expanded: isLargeScreen }
		});

		const treeViewRef = ref(null);
		const contextMenuRef = ref(null);
		const contextMenuItems = ref([
			{ id: 'edit', icon: 'edit', text: 'Edit', visible: true },
			{ id: 'delete', icon: 'remove', text: 'Delete', visible: true },
		]);
		const contextItemActive = ref(null);

		function forwardClick(...args) {
			context.emit("click", args);
		}

		function handleItemClick(e) {
			if (!e.itemData.path || props.compactMode) {
				return;
			}
			router.push(e.itemData.path);

			const pointerEvent = e.event;
			pointerEvent.stopPropagation();
		}

		function updateSelection() {
			if (!treeViewRef.value || !treeViewRef.value.instance) {
				return;
			}

			treeViewRef.value.instance.selectItem(route.path);
			treeViewRef.value.instance.expandItem(route.path);
		}

		onMounted(() => {
			updateSelection();
			if (props.compactMode) {
				treeViewRef.value.instance.collapseAll();
			}
		});

		watch(() => route.path,
			() => { updateSelection(); }
		);

		watch(() => props.compactMode,
			() => {
				if (props.compactMode) {
					treeViewRef.value.instance.collapseAll();
				} else {
					updateSelection();
				}
			}
		);

		// Wait for our group cache to be ready, then replace the placeholder in app-navigation with our list and refresh.
		// TODO: Seems to have a bug on initial sign-in where we get 401 errors and menu doesn't load!
		GroupCache.waitForData().then(() => {
			console.log("SideNavMenu: GroupCache data is ready.");
			let idx = items.findIndex(i => i.placeholder && i.placeholder == 'groupmenu');
			if (idx != -1) {
				items[idx].visible = false;
				items.splice(idx, 0, ...GroupCache.getMenuTree());
				if (treeViewRef.value && treeViewRef.value.instance && typeof treeViewRef.value.instance.getDataSource === 'function') {
					treeViewRef.value.instance.getDataSource()?.reload();
				}
			}

			// Now we can watch for changes to the group cache and update the menu tree by removing all existing items and replacing with new ones.
			watch(GroupCache.getCacheRef(), () => {
				console.log("SideNavMenu: GroupCache data changed.");
				// Record all groups that were expanded before we start our update.
				let expandedGroups = [];
				for (let i = 0; i < items.length; i++) {
					if (items[i]['_menuTree'] && items[i]['expanded']) {
						expandedGroups.push(items[i]['_menuTree'][0].id);
					}
				}
				
				// We need to remove all existing items with a _menuTree property, but we can't reassign items because it's used in the data source (so no filter, etc.)
				for (let i = items.length - 1; i >= 0; i--) {
					if (items[i]['_menuTree']) {
						items.splice(i, 1);
					}
				}
				let idx = items.findIndex(i => i.placeholder && i.placeholder == 'groupmenu');
				if (idx != -1) {
					items.splice(idx, 0, ...GroupCache.getMenuTree());
				}

				// Now expand all groups that were expanded before the update, and refresh the tree view.
				for (let i = 0; i < items.length; i++) {
					if (items[i]['_menuTree'] && expandedGroups.includes(items[i]['_menuTree'][0].id)) {
						items[i].expanded = true;
						console.log("Expanding group " + items[i]['_menuTree'][0].id);
					}
				}
				if (treeViewRef.value && treeViewRef.value.instance) {
					treeViewRef.value.instance.getDataSource().reload();
				}
			});
		});
		
		// Handle right click on menu item to render a context menu.
		function treeViewContextMenu(e) {
			let contextItem = e.itemData['_menuTree'] ?? null;
			let contextIsGroup = contextItem && contextItem[1] == null;
			contextItemActive.value = !contextItem ? null : (contextIsGroup ? contextItem[0] : contextItem[1]);
			contextMenuItems.value.find(i => i.id == 'edit').visible = contextItem && !contextIsGroup;
			contextMenuItems.value.find(i => i.id == 'delete').visible = contextItem;
		}

		// Handle click on actual context menu entry.
		function contextMenuItemClick(e) {
			if (e.itemData.id == 'edit' && contextItemActive.value) {
				router.push(`/layout/${contextItemActive.value.getSlugPath()}`);
			} else if (e.itemData.id == 'delete' && contextItemActive.value) {
				let isPage = contextItemActive.value instanceof Page;
				router.push(`/delete/${isPage ? 'page' : 'group'}/${contextItemActive.value.id}`);
			}
		}

		return {
			treeViewRef,
			contextMenuRef,
			items,
			contextMenuItems,
			forwardClick,
			handleItemClick,
			updateSelection,
			treeViewContextMenu,
			contextMenuItemClick
		};
	},
	components: {
		DxTreeView,
		DxContextMenu
	}
};
</script>

<style lang="scss">
@import "../dx-styles.scss";
@import "../themes/generated/variables.additional.scss";

.side-navigation-menu {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	height: 100%;
	width: 250px !important;

	.menu-container {
		min-height: 100%;
		display: flex;
		flex: 1;

		.dx-treeview {
			// ## Long text positioning
			white-space: nowrap;
			// ##

			// ## Icon width customization
			.dx-treeview-item {
				padding-left: 0;
				flex-direction: row-reverse;

				.dx-icon {
					width: $side-panel-min-width !important;
					margin: 0 !important;
				}
			}

			.dx-treeview-expander-icon-stub {
				display: none;
			}

			// ##

			// ## Arrow customization
			.dx-treeview-node {
				padding: 0 0 !important;
			}

			.dx-treeview-toggle-item-visibility {
				right: 10px;
				left: auto;
			}

			.dx-rtl .dx-treeview-toggle-item-visibility {
				left: 10px;
				right: auto;
			}

			// ##

			// ## Item levels customization
			.dx-treeview-node {
				&[aria-level="1"] {
					font-weight: bold;
					border-bottom: 1px solid $base-border-color;
				}

				&[aria-level="2"] .dx-treeview-item-content {
					font-weight: normal;
					padding: 0 $side-panel-min-width 0 0;
					margin-left: 17px;

					.dx-icon {
						width: calc($side-panel-min-width - 17px) !important;
					}
				}
			}

			// ##
		}

		// ## Selected & Focuced items customization
		.dx-treeview {
			.dx-treeview-node-container {
				.dx-treeview-node {
					&.dx-state-selected:not(.dx-state-focused)>.dx-treeview-item {
						background: transparent;
					}

					&.dx-state-selected>.dx-treeview-item * {
						color: $base-accent;
					}

					&:not(.dx-state-focused)>.dx-treeview-item.dx-state-hover {
						background-color: lighten($base-bg, 4);
					}
				}
			}
		}

		.dx-theme-generic .dx-treeview {
			.dx-treeview-node-container .dx-treeview-node.dx-state-selected.dx-state-focused>.dx-treeview-item * {
				color: inherit;
			}
		}

		// ##
	}
}
</style>
